import { Badge, Tooltip, TooltipContent, TooltipTrigger } from '@repo/shared-client/components/ui'
import { cn } from '@repo/shared-client/lib/utils'
import { useTranslation } from '@web/src/lib/intl/react'
import { type ComponentProps, forwardRef } from 'react'
import { enumToString } from 'src/lib/utilsClient'
import type { ReviewEnum } from 'types/graphql'

interface SFReviewHeaderProps extends ComponentProps<'div'> {
  reviewStatus: ReviewEnum
  showUnreviewed?: boolean
}

function reviewHeaderBarClasses(reviewStatus: ReviewEnum): string {
  if (reviewStatus === 'UNREVIEWED') {
    return 'bg-card text-foreground'
  }
  if (reviewStatus === 'REJECTED') {
    return 'bg-destructive text-destructive-foreground'
  }
  return 'bg-amber-400 text-black dark:bg-amber-400 dark:text-black'
}

const SFReviewHeader = forwardRef<HTMLDivElement, SFReviewHeaderProps>((props, forwardedRef) => {
  const { reviewStatus, showUnreviewed: _showUnreviewed = true, className, ...rest } = props
  const { t } = useTranslation()

  if (reviewStatus === 'ACCEPTED') {
    return null
  }

  const label = <span className="font-bold uppercase">{enumToString(reviewStatus)}</span>

  return (
    <div
      className={cn('flex w-full justify-center p-1', reviewHeaderBarClasses(reviewStatus), className)}
      ref={forwardedRef}
      {...rest}
    >
      {reviewStatus === 'NEEDS_REVIEW' ? (
        <Tooltip>
          <TooltipTrigger render={<span className="inline-flex cursor-default justify-center">{label}</span>} />
          <TooltipContent>
            {t('Admins need to review and approve this before it can be seen by other users.')}
          </TooltipContent>
        </Tooltip>
      ) : (
        label
      )}
    </div>
  )
})

SFReviewHeader.displayName = 'SFReviewHeader'

interface SFReviewStatusProps extends ComponentProps<typeof Badge> {
  reviewStatus: ReviewEnum
  showUnreviewed?: boolean
}

const SFReviewStatus = forwardRef<HTMLSpanElement, SFReviewStatusProps>((props, forwardedRef) => {
  const { reviewStatus, showUnreviewed = true, className, variant: variantProp, size = 'default', ...rest } = props
  const { t } = useTranslation()

  let displayReview = false

  if (reviewStatus) {
    if (showUnreviewed) {
      if (reviewStatus !== 'ACCEPTED') {
        displayReview = true
      }
    } else if (reviewStatus !== 'ACCEPTED' && reviewStatus !== 'UNREVIEWED') {
      displayReview = true
    }
  }

  const variant =
    variantProp ??
    (reviewStatus === 'REJECTED' ? 'destructive' : reviewStatus === 'UNREVIEWED' ? 'secondary' : 'outline')

  const badge = (
    <Badge
      className={cn(
        'shadow-sm',
        reviewStatus === 'NEEDS_REVIEW' &&
          'border-amber-500/40 bg-amber-500/15 text-amber-950 dark:border-amber-400/40 dark:bg-amber-400 dark:text-black',
        reviewStatus === 'UNREVIEWED' && 'border-border bg-background text-foreground',
        !displayReview && 'hidden',
        className
      )}
      ref={forwardedRef}
      size={size}
      variant={variant}
      {...rest}
    >
      {enumToString(reviewStatus)}
    </Badge>
  )

  if (reviewStatus === 'NEEDS_REVIEW') {
    return (
      <Tooltip>
        <TooltipTrigger render={badge} />
        <TooltipContent>
          {t('Admins need to review and approve this before it can be seen by other users.')}
        </TooltipContent>
      </Tooltip>
    )
  }

  return badge
})

SFReviewStatus.displayName = 'SFReviewStatus'

export default SFReviewStatus

export { SFReviewHeader }
